import React from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createContainer,
	createHook,
	createStateHook,
	type Action as ActionBase,
} from '@atlassian/react-sweet-state';

type RefKey = string | symbol;

// FIXME: key can be undefined(by a fact), and undefined IS a valid "access key". This potentially pollutes global state
type UnsafeRefKey = RefKey | undefined;

type Store = {
	[key: RefKey]: HTMLElement | undefined;
};

type Action = ActionBase<Store>;

type Actions = {
	register: (key: UnsafeRefKey, ref: HTMLElement | undefined | null) => Action;
};

const store = createStore<Store, Actions>({
	initialState: {},
	actions: {
		register:
			(
				key: any, // eslint-disable-line @typescript-eslint/no-explicit-any
				ref,
			) =>
			({ setState }) => {
				setState({ [key]: ref || undefined });
			},
	},
	name: 'refs-store',
});

export const GlobalRefContainer = createContainer<Store, Actions, undefined>(store);

const useGlobalRefStateSelector = createStateHook(store, {
	selector: (state, key: RefKey) => state[key],
});

/**
 * Get a global ref that was created with `useGlobalRefStoreActions()[1].register(key, value)`.
 * @param key - The unique key or imported Symbol() for this item.
 * @example
 * ```tsx
 * import MY_REF_KEY from '../example';
 * const [myRef] = useGlobalRef(MY_REF_KEY);
 * ```
 */
export const useGlobalRef = (key: UnsafeRefKey): [HTMLElement | undefined] => [
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	useGlobalRefStateSelector(key as any),
];

/**
 * Get a hook to register a global ref. This ref can be accessed by key from anywhere else in the
 * app using `useGlobalRef()`.
 * @example
 * Register your global ref with:
 * ```tsx
 * export const MY_REF_KEY = Symbol('MY_REF_KEY'); // or just "MY_REF_KEY";
 * const [, { register }] = useGlobalRefStoreActions();
 * register(MY_REF_KEY, myRef);
 * ```
 */
export const useGlobalRefStoreActions = createHook(store, {
	selector: null,
});

type InjectedRef<RefProp extends string> = Record<RefProp, HTMLElement | undefined>;

/**
 * @deprecated for class component usage. Prefer {@link useGlobalRef} in all cases
 * @param propName - a name of the prop to be injected
 * @param refName - a name of a ref to be injected
 * @example
 * ```tsx
 *   withGlobalRef('elementRef', MY_REF_KEY)(MyComponent);
 *   // is equal to
 *   const [ref] = useGlobalRef(MY_REF_KEY);
 *   <MyComponent elementRef={myRef}>
 * ```
 */
export const withGlobalRef =
	<RefProp extends string>(propName: RefProp, refName: RefKey) =>
	<P extends {}>(Component: React.ComponentType<P & InjectedRef<RefProp>>): React.FC<P> =>
	(props) => {
		const [ref] = useGlobalRef(refName);
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return <Component {...props} {...({ [propName]: ref } as InjectedRef<RefProp>)} />;
	};
